import { createContext, useContext, useReducer, useCallback, useMemo } from 'react';

import { WHAT_NEW_BANNER_LIST } from '#client/constants/whatNewBanner';

export const WhatsNewBannerContext = createContext();
WhatsNewBannerContext.displayName = 'WhatsNewBannerContext';

const useWhatsNewBannerContext = () => {
  const context = useContext(WhatsNewBannerContext);

  if (typeof context === 'undefined') {
    throw new Error(
      'useWhatsNewBannerContext must be used within a WhatsNewBannerContext.Provider'
    );
  }

  return context;
};

const reducer = (prevState, action) => {
  switch (action.type) {
    case 'UPDATE_DATA':
      return action.payload;
    case 'REMOVE_BANNER':
      if (!prevState.whatNewBanners.find(({ type }) => type === action.payload)) {
        return prevState;
      }

      return {
        ...prevState,
        whatNewBanners: prevState.whatNewBanners.filter(({ type }) => type !== action.payload),
        activeStep: 0
      };
    case 'SET_ACTIVE_STEP':
      return {
        ...prevState,
        activeStep: action.payload
      };
    default:
      return prevState;
  }
};

export function WhatsNewBannerProvider({ children }) {
  const [{ whatNewBanners, activeStep }, dispatch] = useReducer(reducer, {
    whatNewBanners: WHAT_NEW_BANNER_LIST,
    activeStep: 0
  });

  const setActiveStep = useCallback((step) => {
    dispatch({
      type: 'SET_ACTIVE_STEP',
      payload: step
    });
  }, []);

  /**
   * This function removes a banner from the array
   * @param {string} type The type of the banner to remove
   */
  const removeBanner = useCallback(
    (type) => {
      // check if type in banner list
      if (!whatNewBanners.find((banner) => banner.type === type)) {
        return;
      }

      dispatch({
        type: 'REMOVE_BANNER',
        payload: type
      });
    },
    [whatNewBanners]
  );

  const value = useMemo(
    () => ({
      whatNewBanners,
      activeStep,
      setActiveStep,
      removeBanner
    }),
    [whatNewBanners, activeStep, setActiveStep, removeBanner]
  );

  return <WhatsNewBannerContext.Provider value={value}>{children}</WhatsNewBannerContext.Provider>;
}

export default useWhatsNewBannerContext;
